import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useAddCustomerAgreement } from 'hooks/mutations/useAddCustomerAgreement'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { customerModelAtom } from 'pages/Customers/atoms/customersAtoms'
import type { BaseErrorResponse } from 'types/beneficiaries'
import { CustomerEntityType, type CreateCustomerAgreement } from 'types/customers'
import type { ErrorDetails } from 'types/types'
import { ConfirmDetailsLayout } from './ConfirmDetailsLayout'

export interface AddCustomerAgreementConfirmDetailsProps {
  formData: CreateCustomerAgreement
  displayData: Partial<Record<keyof CreateCustomerAgreement, string>>
  onSuccess: () => void
  onCancel: () => void
  onValidationError: (errors: ErrorDetails) => void
}

export const AddCustomerAgreementConfirmDetails = ({
  formData,
  displayData,
  onSuccess,
  onCancel,
  onValidationError,
}: AddCustomerAgreementConfirmDetailsProps) => {
  const { t } = useTranslation()
  const { requestError, resetRequestError, handleRequestError } = useMappedErrorMessage()
  const { mutate: submitAgreement, isPending: isSubmitting } = useAddCustomerAgreement()
  const customerModel = useAtomValue(customerModelAtom)
  const isIndividual = formData?.customerType === CustomerEntityType.INDIVIDUAL

  const onConfirm = () => {
    resetRequestError()
    submitAgreement(
      { ...formData, model: customerModel },
      {
        onSuccess,
        onError: (error: BaseErrorResponse) => {
          const fieldErrors = error?.data?.details?.errors
          const hasFieldErrors = Object.keys(fieldErrors || {}).length > 0
          if (hasFieldErrors && fieldErrors) {
            onValidationError(fieldErrors)
          }
          handleRequestError(
            error,
            '/platform/v2/agreements',
            'useAddCustomerAgreement',
            hasFieldErrors
          )
        },
      }
    )
  }

  const confirmItems = [
    {
      id: 'customerName',
      title: isIndividual ? t('customers.fullName') : t('customers.businessName'),
      body: formData.customerName,
    },
    { id: 'reference', title: t('customers.externalReference'), body: formData.reference },
    { id: 'useCase', title: t('customers.useCase'), body: displayData.useCase || formData.useCase },
    {
      id: 'countryCode',
      title: t('customers.countryCode'),
      body: displayData.countryCode || formData.countryCode,
    },
  ]

  return (
    <ConfirmDetailsLayout
      confirmItems={confirmItems}
      requestError={requestError}
      isSubmitting={isSubmitting}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  )
}
